import { useState } from "react";
import {
  useDeviceOperation,
  useEjectRepair,
  type BatteryRow,
  type CabinetRow,
} from "../lib/api.js";

const faultLabel: Record<number, string> = {
  0: "ok",
  1: "virtual power",
  2: "Apple connector",
  3: "USB connector",
  4: "USB-C",
  5: "swelling",
  6: "auth failed",
  7: "cannot charge",
  8: "duplicate id",
  9: "recall",
  10: "health substandard",
  11: "overuse limit",
};

function slotTone(b: BatteryRow | undefined): "empty" | "ok" | "warn" | "bad" {
  if (!b) return "empty";
  const f = b.faultType ?? 0;
  if (f === 0) return "ok";
  if (f >= 7) return "bad";
  return "warn";
}

const toneClass: Record<"empty" | "ok" | "warn" | "bad", string> = {
  empty: "border-dashed border-zinc-800/80 bg-zinc-950/30 text-zinc-600",
  ok: "border-emerald-500/30 bg-emerald-500/5 text-emerald-200",
  warn: "border-amber-500/40 bg-amber-500/10 text-amber-100",
  bad: "border-rose-500/40 bg-rose-500/10 text-rose-100",
};

export function SlotGrid({
  cabinet,
  batteries,
}: {
  cabinet: CabinetRow;
  batteries: BatteryRow[];
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const op = useDeviceOperation();
  const repair = useEjectRepair();

  const total = cabinet.totalCount ?? Math.max(0, ...batteries.map((b) => b.slot ?? 0));
  const bySlot = new Map<number, BatteryRow>();
  for (const b of batteries) {
    if (b.slot !== null) bySlot.set(b.slot, b);
  }
  const slots = Array.from({ length: total }, (_, i) => i + 1);
  const sel = selected !== null ? bySlot.get(selected) : undefined;

  return (
    <div className="card p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-zinc-200">Slot grid</h2>
        <span className="text-xs text-zinc-500">{batteries.length} of {total} occupied</span>
      </div>

      <div className="grid grid-cols-6 gap-2 sm:grid-cols-8 lg:grid-cols-12">
        {slots.map((slot) => {
          const b = bySlot.get(slot);
          const tone = slotTone(b);
          const active = selected === slot;
          return (
            <button
              key={slot}
              type="button"
              onClick={() => setSelected((s) => (s === slot ? null : slot))}
              className={
                "flex aspect-square flex-col items-center justify-center rounded-md border text-[10px] font-mono transition " +
                toneClass[tone] +
                (active ? " ring-2 ring-offset-2 ring-offset-zinc-950 ring-zinc-200" : "")
              }
              title={b ? `${b.id} · ${b.vol ?? "?"}%` : "empty"}
            >
              <span className="text-zinc-500">{slot}</span>
              {b && <span className="mt-0.5">{b.vol ?? "—"}%</span>}
            </button>
          );
        })}
      </div>

      <div className="mt-4 min-h-[88px] rounded-lg border border-zinc-800/60 bg-zinc-950/50 p-4 text-xs">
        {sel ? (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="font-mono">
              <div className="text-zinc-500">Slot {selected} · battery</div>
              <div className="mt-0.5 text-zinc-100">{sel.id}</div>
              <div className="mt-1 text-zinc-400">
                {sel.vol ?? "?"}% charge · {faultLabel[sel.faultType ?? 0] ?? `fault ${sel.faultType}`}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() =>
                  op.mutate({
                    cabinetId: cabinet.id,
                    operationType: "pop",
                    slotNum: selected ?? 0,
                    reason: "ops",
                  })
                }
                disabled={op.isPending}
                className="rounded-md bg-zinc-800 px-2.5 py-1 text-zinc-200 ring-1 ring-zinc-700 transition hover:bg-zinc-700 disabled:opacity-50"
              >
                Pop
              </button>
              <button
                onClick={() =>
                  op.mutate({
                    cabinetId: cabinet.id,
                    operationType: "lock",
                    slotNum: selected ?? 0,
                    reason: "ops",
                  })
                }
                disabled={op.isPending}
                className="rounded-md bg-zinc-800 px-2.5 py-1 text-zinc-200 ring-1 ring-zinc-700 transition hover:bg-zinc-700 disabled:opacity-50"
              >
                Lock
              </button>
              <button
                onClick={() =>
                  op.mutate({
                    cabinetId: cabinet.id,
                    operationType: "unlock",
                    slotNum: selected ?? 0,
                    reason: "ops",
                  })
                }
                disabled={op.isPending}
                className="rounded-md bg-zinc-800 px-2.5 py-1 text-zinc-200 ring-1 ring-zinc-700 transition hover:bg-zinc-700 disabled:opacity-50"
              >
                Unlock
              </button>
              <button
                onClick={() => repair.mutate({ batteryId: sel.id })}
                disabled={repair.isPending}
                className="rounded-md bg-rose-500/20 px-2.5 py-1 text-rose-200 ring-1 ring-rose-500/40 transition hover:bg-rose-500/30 disabled:opacity-50"
              >
                Eject for repair
              </button>
            </div>
          </div>
        ) : (
          <span className="text-zinc-500">Click a slot to inspect or act on it.</span>
        )}
      </div>
    </div>
  );
}
